import { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../services/supabase'
import { useCurrentAdvisor } from '../../hooks/useCurrentAdvisor'
import { RotateCcw, Trash2, RefreshCw } from 'lucide-react'

export function TaskBin({ isDarkMode, onRestored }) {
  const { advisor: currentAdvisor } = useCurrentAdvisor()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [actionId, setActionId] = useState(null) // task being acted on

  const muted = isDarkMode ? 'text-slate-400' : 'text-gray-500'
  const border = isDarkMode ? 'border-slate-700' : 'border-gray-200'
  const rowBg = isDarkMode ? 'bg-slate-800 hover:bg-slate-750' : 'bg-white hover:bg-gray-50'
  const headBg = isDarkMode ? 'bg-slate-900 text-slate-400' : 'bg-gray-50 text-gray-500'

  const fetchBin = useCallback(async () => {
    setLoading(true)
    const { data, error: err } = await supabase
      .from('sfp_tasks')
      .select(`
        id, title, claim_number, client_name, delete_reason, deleted_at,
        deleted_by_advisor:deleted_by(full_name),
        advisor_id
      `)
      .not('deleted_at', 'is', null)
      .order('deleted_at', { ascending: false })
      .limit(200)

    if (err) { setError(err.message) } else { setItems(data ?? []) }
    setLoading(false)
  }, [])

  useEffect(() => { fetchBin() }, [fetchBin])

  const handleRestore = async (task) => {
    setActionId(task.id)
    const { error: err } = await supabase.from('sfp_tasks').update({
      deleted_at:    null,
      deleted_by:    null,
      delete_reason: null,
    }).eq('id', task.id)
    if (!err) {
      setItems(prev => prev.filter(t => t.id !== task.id))
      onRestored?.()
    }
    setActionId(null)
  }

  const handlePermanentDelete = async (task) => {
    // Only the advisor who deleted it (or owner) can permanently delete
    if (!window.confirm(`Permanently delete "${task.claim_number || task.title}"? This cannot be undone.`)) return
    setActionId(task.id)
    const { error: err } = await supabase.from('sfp_tasks').delete().eq('id', task.id)
    if (!err) setItems(prev => prev.filter(t => t.id !== task.id))
    setActionId(null)
  }

  if (loading) return (
    <div className={`flex items-center justify-center h-48 text-sm ${muted}`}>Loading bin…</div>
  )
  if (error) return (
    <div className="m-4 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>
  )

  return (
    <div className={`flex flex-col h-full overflow-hidden ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between px-4 py-3 border-b flex-shrink-0 ${border} ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="flex items-center gap-2">
          <Trash2 className={`w-4 h-4 ${muted}`} />
          <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-200' : 'text-gray-700'}`}>
            Bin ({items.length})
          </span>
          <span className={`text-xs ${muted}`}>— Tasks moved to bin. Restore or permanently delete.</span>
        </div>
        <button
          onClick={fetchBin}
          className={`p-1.5 rounded transition ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-gray-400 hover:text-gray-600'}`}
          title="Refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {items.length === 0 ? (
        <div className={`flex flex-col items-center justify-center h-48 gap-2 ${muted}`}>
          <Trash2 className="w-8 h-8 opacity-30" />
          <p className="text-sm">Bin is empty</p>
        </div>
      ) : (
        <div className="flex-1 overflow-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className={`text-left uppercase tracking-wide text-xs ${headBg}`}>
                <th className="px-4 py-2.5 font-medium">Task</th>
                <th className="px-3 py-2.5 font-medium">Deleted By</th>
                <th className="px-3 py-2.5 font-medium">Reason</th>
                <th className="px-3 py-2.5 font-medium">Date Deleted</th>
                <th className="px-3 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(task => {
                const isActing = actionId === task.id
                const canPermanentDelete = currentAdvisor && (task.deleted_by === currentAdvisor.id || task.advisor_id === currentAdvisor.id)
                return (
                  <tr key={task.id} className={`border-b transition-colors ${border} ${rowBg}`}>
                    <td className="px-4 py-3">
                      <p className={`font-medium truncate max-w-[180px] ${isDarkMode ? 'text-slate-200' : 'text-gray-800'}`}>
                        {task.claim_number || task.title}
                      </p>
                      {task.client_name && (
                        <p className={`text-xs truncate max-w-[180px] ${muted}`}>{task.client_name}</p>
                      )}
                    </td>
                    <td className={`px-3 py-3 ${muted}`}>
                      {task.deleted_by_advisor?.full_name ?? '—'}
                    </td>
                    <td className={`px-3 py-3 ${muted} max-w-[160px]`}>
                      <span className="truncate block" title={task.delete_reason}>
                        {task.delete_reason || '—'}
                      </span>
                    </td>
                    <td className={`px-3 py-3 ${muted} whitespace-nowrap`}>
                      {task.deleted_at ? new Date(task.deleted_at).toLocaleDateString('en-ZA') : '—'}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleRestore(task)}
                          disabled={isActing}
                          className="flex items-center gap-1 px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50 transition"
                          title="Restore task"
                        >
                          <RotateCcw className="w-3 h-3" />
                          {isActing ? '…' : 'Restore'}
                        </button>
                        <button
                          onClick={() => handlePermanentDelete(task)}
                          disabled={isActing || !canPermanentDelete}
                          className="flex items-center gap-1 px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50 transition"
                          title={canPermanentDelete ? 'Permanently delete' : 'Only the deleter or task owner can permanently delete'}
                        >
                          <Trash2 className="w-3 h-3" />
                          {isActing ? '…' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
